import React, {useEffect, useState} from "react";
import {PencilIcon, CheckIcon, XMarkIcon} from "@heroicons/react/24/outline";

export interface EditableSettingRowProps {
    name: string;
    value?: string;
    placeholder?: string;
    onSave: (value: string) => Promise<void>;
    inputType?: React.HTMLInputTypeAttribute;
    validation?: (value: string) => boolean;
    validationErrorMessage?: string;
    showError?: boolean;
}

export function EditableSettingRow({
                                       name,
                                       value,
                                       placeholder,
                                       onSave,
                                       inputType = "text",
                                       validation,
                                       validationErrorMessage = "Ongeldige invoer",
                                       showError = false,
                                   }: EditableSettingRowProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value || "");
    const [originalValue, setOriginalValue] = useState(value || "");
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setCurrentValue(value || "");
        setOriginalValue(value || "");
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setCurrentValue(newValue);

        if (validation) {
            if (!validation(newValue)) {
                setError(validationErrorMessage);
                return;
            } else {
                setError(null);
            }
        } else {
            setError(null);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
        setOriginalValue(currentValue);
    };

    const handleCancel = () => {
        setCurrentValue(originalValue);
        setError(null);
        setIsEditing(false);
    };

    const handleSave = async () => {
        if (validation && !validation(currentValue)) {
            setError(validationErrorMessage);
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            await onSave(currentValue);
            setOriginalValue(currentValue);
            setIsEditing(false);
        } catch (error) {
            setError(error instanceof Error ? error.message : "Er is een fout opgetreden bij het opslaan");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className={`justify-between bg-gray-50 p-3 rounded-md sm:flex ${showError ? 'ring-1 ring-red-300' : ''}`}>
            <dt className="font-medium text-gray-900 sm:w-64 sm:pr-6">
                {name}
            </dt>
            <dd className="mt-1 flex flex-col gap-1 sm:mt-0">
                <div className="flex items-center gap-2">
                    {isEditing ? (
                        <input
                            type={inputType}
                            className={`block w-full rounded-md border-0 px-2 py-1 text-gray-900 shadow-sm ring-1 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary text-sm leading-6 ${
                                error ? 'ring-red-300 focus:ring-red-500' : 'ring-gray-300'
                            }`}
                            value={currentValue}
                            onChange={handleChange}
                            placeholder={placeholder}
                        />
                    ) : (
                        <span className={`block w-full rounded-md border-0 px-2 py-1 text-sm leading-6 ${
                            currentValue ? 'text-gray-900' : 'text-gray-500'
                        }`}>
                            {currentValue || placeholder}
                        </span>
                    )}
                    {!isEditing ? (
                        <button
                            onClick={handleEdit}
                            className="flex-shrink-0 text-gray-400 hover:text-gray-600"
                            aria-label="Edit"
                        >
                            <PencilIcon className="h-4 w-4" />
                        </button>
                    ) : (
                        <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                                onClick={handleSave}
                                disabled={!!error || isSaving}
                                className="text-green-600 hover:text-green-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                                aria-label="Save"
                            >
                                <CheckIcon className={`h-4 w-4 ${isSaving ? 'opacity-50' : ''}`} />
                            </button>
                            <button
                                onClick={handleCancel}
                                disabled={isSaving}
                                className="text-red-600 hover:text-red-700 disabled:text-gray-300 disabled:cursor-not-allowed"
                                aria-label="Cancel"
                            >
                                <XMarkIcon className={`h-4 w-4 ${isSaving ? 'opacity-50' : ''}`} />
                            </button>
                        </div>
                    )}
                </div>
                {error && <p className="text-red-500 text-xs">{error}</p>}
            </dd>
        </div>
    );
}
